import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import {
  getPetAssetKey,
  Species,
  speciesAssetsRatio,
  speciesChatbot,
} from './species';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import { buildSender } from '../sender';

/**
 * Assets for fullenergy messages
 */
export const giftAssets = {
  'background/gift': 'private/chatbot/gift/background.png',
  'background/gift/batched': 'private/chatbot/gift/background_batched.png',
};

/**
 * Create gift message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const giftMessage = createFlexMessageTemplate(
  SB.object({
    sender: SB.object({
      photo: SB.string(),
      name: SB.string(),
      species: SB.tuple(speciesChatbot),
    }),
    locale: SB.tuple(['en', 'ja']),
  }),
  (data, payload, api) => {
    const sender = data.sender;
    const text = getLocaleConfig(data.locale).gift.bPoints.text.replace(
      '{name}',
      sender.name,
    );

    const icon: Species = api.getAssetPath(
      getPetAssetKey(sender.species),
    ) as Species;

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('background/gift'),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.51:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            position: 'absolute',
            width: '80%',
            height: '70%',
            offsetBottom: '32%',
            alignItems: 'center',
            justifyContent: 'flex-end',
            contents: [
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'image',
                    url: icon,
                    aspectRatio: speciesAssetsRatio[icon],
                    aspectMode: 'fit',
                    position: 'absolute',
                    gravity: 'bottom',
                    align: 'center',
                    size: 'full',
                  },
                ],
                position: 'absolute',
                width: '35%',
                height: '100%',
                justifyContent: 'flex-end',
                alignItems: 'center',
              },
            ],
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: sender.photo,
                aspectRatio: '1:1',
                aspectMode: 'cover',
                gravity: 'center',
                size: 'full',
              },
            ],
            position: 'absolute',
            height: '32%',
            width: '21.5%',
            offsetTop: '6.3%',
            offsetStart: '4%',
            cornerRadius: 'xl',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text,
                color: '#ffffff',
                wrap: true,
                align: 'start',
                size: '14px',
                weight: 'bold',
                position: 'relative',
                gravity: 'center',
                scaling: true,
                maxLines: 2,
                adjustMode: 'shrink-to-fit',
              },
            ],
            position: 'absolute',
            width: '65%',
            height: '16%',
            offsetTop: '8%',
            offsetStart: '29%',
            justifyContent: 'center',
            alignItems: 'flex-start',
          },
        ],
        paddingAll: '0px',
      },
    });

    // Add sender to message
    template['sender'] = buildSender({
      name: sender.name,
      iconUrl: sender.photo,
    });

    return template;
  },
);

/**
 * Create gift message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const giftMessageBatched = createFlexMessageTemplate(
  SB.object({
    count: SB.number(),
    locale: SB.tuple(['en', 'ja']),
  }),
  (data, payload, api) => {
    const text = getLocaleConfig(data.locale).gift.count.text.replace(
      '{count}',
      data.count.toFixed(0),
    );

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('background/gift/batched'),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.51:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text,
                color: '#ffffff',
                wrap: true,
                align: 'start',
                size: '20px',
                weight: 'bold',
                position: 'relative',
                gravity: 'center',
                scaling: true,
                maxLines: 1,
                adjustMode: 'shrink-to-fit',
              },
            ],
            position: 'absolute',
            width: '100%',
            height: '16%',
            offsetTop: '2%',
            justifyContent: 'center',
            alignItems: 'center',
          },
        ],
        paddingAll: '0px',
      },
    });

    return template;
  },
);
